import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { Upload, X, Camera } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BloodSugarDialog = ({ isOpen, onClose, patientId, onUpdate, editingRecord = null }) => {
  const [formData, setFormData] = useState({
    date: editingRecord?.date || format(new Date(), 'yyyy-MM-dd'),
    time: editingRecord?.time || format(new Date(), 'HH:mm'),
    reading: editingRecord?.reading || '',
    meal_context: editingRecord?.meal_context || 'Fasting',
    notes: editingRecord?.notes || '',
    image_url: editingRecord?.image_url || '',
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(editingRecord?.image_url || '');
  const [uploading, setUploading] = useState(false);

  React.useEffect(() => {
    if (editingRecord) {
      setFormData({
        date: editingRecord.date || format(new Date(), 'yyyy-MM-dd'),
        time: editingRecord.time || format(new Date(), 'HH:mm'),
        reading: editingRecord.reading || '',
        meal_context: editingRecord.meal_context || 'Fasting',
        notes: editingRecord.notes || '',
        image_url: editingRecord.image_url || '',
      });
      setImagePreview(editingRecord.image_url || '');
    } else {
      setFormData({
        date: format(new Date(), 'yyyy-MM-dd'),
        time: format(new Date(), 'HH:mm'),
        reading: '',
        meal_context: 'Fasting',
        notes: '',
        image_url: '',
      });
      setImagePreview('');
    }
  }, [editingRecord, isOpen]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return null;
    
    setUploading(true);
    const token = localStorage.getItem('token');
    const formDataUpload = new FormData();
    formDataUpload.append('file', imageFile);

    try {
      const response = await axios.post(`${API}/upload-image`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setUploading(false);
      return response.data.image_url;
    } catch (error) {
      setUploading(false);
      toast.error('Failed to upload image');
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      // Upload image first if one was selected
      let imageUrl = formData.image_url; // Keep existing URL if editing
      if (imageFile) {
        imageUrl = await uploadImage();
        if (!imageUrl) {
          toast.error('Failed to upload image. Please try again.');
          return;
        }
      }

      if (editingRecord) {
        await axios.put(
          `${API}/blood-sugar/${editingRecord.id}`,
          { ...formData, image_url: imageUrl },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Blood sugar updated successfully');
      } else {
        await axios.post(
          `${API}/blood-sugar`,
          { 
            ...formData, 
            patient_id: patientId, 
            reading: parseFloat(formData.reading),
            image_url: imageUrl 
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Blood sugar recorded successfully');
      }
      
      // Reset form
      setFormData({
        date: format(new Date(), 'yyyy-MM-dd'),
        time: format(new Date(), 'HH:mm'),
        reading: '',
        meal_context: 'Fasting',
        notes: '',
        image_url: '',
      });
      setImageFile(null);
      setImagePreview('');
      
      onClose();
      onUpdate();
    } catch (error) {
      toast.error(editingRecord ? 'Failed to update' : 'Failed to record blood sugar');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editingRecord ? 'Edit Blood Sugar' : 'Record Blood Sugar'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Date</label>
              <input
                type="date"
                className="input w-full"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                max={format(new Date(), 'yyyy-MM-dd')}
                min={format(new Date(new Date().setMonth(new Date().getMonth() - 4)), 'yyyy-MM-dd')}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Time</label>
              <input
                type="time"
                className="input w-full"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Reading (mg/dL) *</label>
              <input
                type="number"
                step="0.1"
                className="input w-full"
                value={formData.reading}
                onChange={(e) => setFormData({ ...formData, reading: e.target.value })}
                placeholder="e.g., 120"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Context</label>
              <select
                className="input w-full"
                value={formData.meal_context}
                onChange={(e) => setFormData({ ...formData, meal_context: e.target.value })}
              >
                <option value="Fasting">Fasting</option>
                <option value="Before meal">Before meal</option>
                <option value="After meal">After meal</option>
                <option value="Bedtime">Bedtime</option>
                <option value="Random">Random</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Attach Image (Optional)
            </label>
            <div className="flex gap-2 mb-2">
              <label className="btn text-sm px-4 py-2 cursor-pointer flex items-center gap-2" style={{ backgroundColor: '#A2DAD3', color: 'white' }}>
                <Camera size={16} />
                Camera
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
              <label className="btn text-sm px-4 py-2 cursor-pointer flex items-center gap-2" style={{ backgroundColor: '#A2DAD3', color: 'white' }}>
                <Upload size={16} />
                Upload
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
            {imagePreview && (
              <div className="flex items-center gap-2">
                <img 
                  src={imagePreview.startsWith('data:') ? imagePreview : `${BACKEND_URL}${imagePreview}`} 
                  alt="Preview" 
                  className="h-32 rounded cursor-pointer border"
                  onClick={() => window.open(imagePreview.startsWith('data:') ? imagePreview : `${BACKEND_URL}${imagePreview}`, '_blank')}
                  title="Click to view full size"
                />
                <button
                  type="button"
                  onClick={() => {
                    setImagePreview('');
                    setImageFile(null);
                    setFormData({ ...formData, image_url: '' });
                  }}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  ✕ Remove
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Notes</label>
            <textarea
              className="input w-full"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes..."
              rows="2"
            />
          </div>

          <button type="submit" className="btn btn-primary w-full" disabled={uploading}>
            {uploading ? 'Uploading Image...' : (editingRecord ? 'Update' : 'Record') + ' Blood Sugar'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BloodSugarDialog;
